import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  TextInput,
  Image,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import http from '../../services/http';
import { setAuthToken } from '../../services/auth/tokenStorage';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ChangePasswordScreen');

// Import icons
import backIcon from '../../assets/icons/back.png';
import lockIcon from '../../assets/icons/lock.png';
import eyeIcon from '../../assets/icons/Eye.png';
import eyeClosedIcon from '../../assets/icons/Eye-closed.png';
import logo from '../../assets/logo.png';

interface ChangePasswordScreenProps {
  navigation: NavigationProp<any>;
}

const ChangePasswordScreen: React.FC<ChangePasswordScreenProps> = ({ navigation }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const validatePassword = (password: string) => {
    return password.length >= 8 && 
           /[A-Z]/.test(password) && 
           /[0-9]/.test(password) &&
           /[!@#$%^&*(),.?":{}|<>]/.test(password);
  };

  useEffect(() => {
    const isValidNewPassword = validatePassword(newPassword);
    const passwordsMatch = newPassword === confirmPassword;
    const hasCurrentPassword = currentPassword.length > 0;
    setIsValid(isValidNewPassword && passwordsMatch && hasCurrentPassword);
  }, [currentPassword, newPassword, confirmPassword]);

  // K23: the server checks the current password, writes the new hash with a
  // session cut-off (every other session ends) and returns a fresh token
  // for this device. Refusals come back as 400s with a plain message.
  const handleChangePassword = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setErrorText(null);
    try {
      const response = await http.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      const accessToken = response.data?.access_token;
      if (typeof accessToken === 'string' && accessToken) {
        await setAuthToken(accessToken);
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      Alert.alert(
        'Password changed',
        'Your password has been changed. Any other device signed in to this account has been signed out.',
        [{ text: 'OK', onPress: () => navigation.goBack() }],
      );
    } catch (error: any) {
      const message =
        typeof error?.response?.data?.message === 'string'
          ? error.response.data.message
          : 'The password could not be changed. Please try again.';
      logger.warn('Password change refused');
      setErrorText(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Change Password</Text>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.logoContainer}>
        <Image source={logo} style={styles.logo} resizeMode="contain" />
      </View>

      <View style={styles.content}>
        <View style={styles.inputGroup}>
          <View style={styles.inputContainer}>
            <Image source={lockIcon} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="Current Password"
              placeholderTextColor="#A9A9A9"
              secureTextEntry={!showCurrentPassword}
              value={currentPassword}
              onChangeText={setCurrentPassword}
            />
            <TouchableOpacity onPress={() => setShowCurrentPassword(!showCurrentPassword)}>
              <Image
                source={showCurrentPassword ? eyeIcon : eyeClosedIcon}
                style={styles.eyeIcon}
              />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.inputGroup}>
          <View style={styles.inputContainer}>
            <Image source={lockIcon} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="New Password"
              placeholderTextColor="#A9A9A9"
              secureTextEntry={!showNewPassword}
              value={newPassword}
              onChangeText={setNewPassword}
            />
            <TouchableOpacity onPress={() => setShowNewPassword(!showNewPassword)}>
              <Image
                source={showNewPassword ? eyeIcon : eyeClosedIcon}
                style={styles.eyeIcon}
              />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.inputGroup}>
          <View style={styles.inputContainer}>
            <Image source={lockIcon} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="Confirm New Password"
              placeholderTextColor="#A9A9A9"
              secureTextEntry={!showConfirmPassword}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
            />
            <TouchableOpacity onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
              <Image
                source={showConfirmPassword ? eyeIcon : eyeClosedIcon}
                style={styles.eyeIcon}
              />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.requirements}>
          <View style={styles.requirementRow}>
            <View style={[styles.checkCircle, validatePassword(newPassword) && styles.checkCircleActive]}>
              <Text style={[styles.checkMark, validatePassword(newPassword) && styles.checkMarkActive]}>✓</Text>
            </View>
            <Text style={styles.requirementText}>Must be 8 characters or longer</Text>
          </View>
          <View style={styles.requirementRow}>
            <View style={[styles.checkCircle, /[A-Z]/.test(newPassword) && styles.checkCircleActive]}>
              <Text style={[styles.checkMark, /[A-Z]/.test(newPassword) && styles.checkMarkActive]}>✓</Text>
            </View>
            <Text style={styles.requirementText}>Must contain at least 1 uppercase</Text>
          </View>
          <View style={styles.requirementRow}>
            <View style={[styles.checkCircle, /[0-9]/.test(newPassword) && styles.checkCircleActive]}>
              <Text style={[styles.checkMark, /[0-9]/.test(newPassword) && styles.checkMarkActive]}>✓</Text>
            </View>
            <Text style={styles.requirementText}>Must contain at least 1 number</Text>
          </View>
          <View style={styles.requirementRow}>
            <View style={[styles.checkCircle, /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) && styles.checkCircleActive]}>
              <Text style={[styles.checkMark, /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) && styles.checkMarkActive]}>✓</Text>
            </View>
            <Text style={styles.requirementText}>Must contain at least 1 special character</Text>
          </View>
        </View>

        {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}

        <TouchableOpacity
          style={[styles.changeButton, isValid && !submitting && styles.changeButtonActive]}
          onPress={handleChangePassword}
          disabled={!isValid || submitting}
        >
          <Text style={[styles.changeButtonText, isValid && !submitting && styles.changeButtonTextActive]}>
            {submitting ? 'Changing…' : 'Change Password'}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  errorText: {
    color: '#D64545',
    fontSize: ms(13),
    marginBottom: vs(10),
    textAlign: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    paddingTop: ms(40),
    paddingBottom: ms(10),
  },
  logo: {
    width: s(250),
    height: s(150),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
    paddingTop: ms(20),
  },
  inputGroup: {
    marginBottom: ms(15),
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    paddingHorizontal: ms(15),
    height: s(45),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  inputIcon: {
    width: s(20),
    height: s(20),
    marginRight: ms(10),
    tintColor: '#29BFC0',
  },
  input: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#333333',
    height: s(45),
    paddingVertical: ms(8),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#666',
  },
  requirements: {
    marginTop: ms(20),
    marginBottom: ms(30),
  },
  requirementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(12),
  },
  checkCircle: {
    width: s(20),
    height: s(20),
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: ms(10),
  },
  checkCircleActive: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  checkMark: {
    color: 'transparent',
    fontSize: ms(12, 0.3),
    fontWeight: 'bold',
  },
  checkMarkActive: {
    color: '#FFFFFF',
  },
  requirementText: {
    fontSize: ms(14, 0.3),
    color: '#666',
  },
  changeButton: {
    backgroundColor: '#E5E7EB',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(20),
  },
  changeButtonActive: {
    backgroundColor: '#29BFC0',
  },
  changeButtonText: {
    color: '#4B5563',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  changeButtonTextActive: {
    color: '#FFFFFF',
  },
});

export default ChangePasswordScreen; 